import { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../models/auth';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth data
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    // Safely parse stored user data
    let parsedUser = null;
    if (storedUser) {
      try {
        // Check if storedUser is a valid non-empty string
        if (storedUser && storedUser !== 'undefined' && storedUser !== 'null' && storedUser.trim() !== '') {
          parsedUser = JSON.parse(storedUser);
          // Validate that parsed data is an object
          if (typeof parsedUser !== 'object' || parsedUser === null || Array.isArray(parsedUser)) {
            parsedUser = null;
          }
        }
      } catch (error) {
        console.warn('Failed to parse stored user data:', error);
        // Clear invalid data
        localStorage.removeItem('user');
        parsedUser = null;
      }
    }

    if (storedToken && parsedUser) {
      setToken(storedToken);
      setUser(parsedUser);
      // Verify token is still valid
      authApi
        .getUser()
        .then((userData) => {
          if (userData) {
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          } else {
            throw new Error('Invalid user data');
          }
        })
        .catch(() => {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      // Clear invalid or missing data
      if (storedToken && !parsedUser) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authApi.login(email, password);
    
    // API response structure: { success: true, message: "...", data: { user: {...}, token: "...", token_type: "Bearer" } }
    // Safely extract token and user data from nested structure
    const token = response?.data?.token || response?.token || null;
    const userData = response?.data?.user || response?.data || null;
    
    if (token && userData) {
      setToken(token);
      setUser(userData);
      try {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
      } catch (error) {
        console.error('Failed to save auth data to localStorage:', error);
        // Re-throw to let the caller handle the error
        throw new Error('Failed to save authentication data');
      }
    } else {
      // Clear any partial data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      throw new Error('Invalid login response: missing token or user data');
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        token,
        loading,
        login,
        logout,
        isAuthenticated: !!token && !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};




